import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    TemplateRef,
    computed,
    inject,
    input,
    output,
} from '@angular/core';
import { TelXLAvatarComponent } from '@dxp/elements';
import {
    IDirectoryListing,
    IDirectoryListingEvent,
    IDirectoryListingSelection,
} from '@dxp/core/models';
import { FormatDurationPipe } from '@dxp/core/pipes';
import { Router } from '@angular/router';
import { secondsToDuration } from '@dxp/core/helpers';
import { AvatarVariant, ListingMode, MediaType } from '@dxp/core/types';

@Component({
    selector: 'telxl-shared-directory-listing-default-item',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TelXLAvatarComponent],
    providers: [FormatDurationPipe],
    templateUrl: './directory-listing-default-item.component.html',
    styleUrl: './directory-listing-default-item.component.scss',
})
export class TelXLDirectoryListingDefaultItemComponent {
    readonly #router = inject(Router);
    readonly #formatDurationPipe = inject(FormatDurationPipe);

    secondsToDuration = secondsToDuration;

    mode = input<ListingMode[]>([]);
    source = input.required<AvatarVariant>();
    item = input.required<IDirectoryListing>();
    customButtonTemplate = input<TemplateRef<any>>();
    displayMediaIcons = input<MediaType[]>([]);
    selected = input<IDirectoryListingSelection>({
        selected: false,
        item: ['voice'],
    });
    view = input<'contact' | 'presence' | 'none'>('contact');
    disabled = input<boolean>(false);
    selectedChange = output<IDirectoryListingEvent>();

    readonly filteredMediaIcons = computed<MediaType[]>(() =>
        this.displayMediaIcons().filter(icon => {
            const item = this.item?.();

            switch (icon) {
                case 'Voice':
                    return item.phoneNumber && item.phoneNumber.length > 0;
                case 'Email':
                    return item.email && item.email.length > 0;
                default:
                    return false;
            }
        }),
    );

    readonly contact = computed<string[]>(() => {
        const item = this.item?.();
        const contacts: string[] = [];

        if (!item) return contacts;

        if (item.phoneNumber && item.phoneNumber.length > 0) {
            contacts.push(`Phone: ${item.phoneNumber}`);
        }

        if (item.email && item.email.length > 0) {
            contacts.push(`Email: ${item.email.toLowerCase()}`);
        }

        return contacts;
    });

    readonly status = computed<string>(() => {
        if (this.item().presence) {
            return `Status: ${this.item().presence?.label} ${this.#formatDurationPipe.transform(secondsToDuration(this.item().presence?.stateDuration ?? 0))}`;
        } else {
            return 'Status: Unavailable';
        }
    });

    select() {
        this.selectedChange.emit({ type: 'custom', item: this.item() });
    }

    iconClass(icon: MediaType): string {
        const classes = [];

        if (
            this.disabled() ||
            (!this.mode().includes('emit') &&
                !this.mode().includes('communicate'))
        ) {
            classes.push('disabled');
        }

        switch (icon) {
            case 'Voice':
                classes.push('fa-phone');
                if (
                    this.selected().selected &&
                    this.selected().item.includes('voice')
                ) {
                    classes.push('fa-solid');
                }
                break;
            case 'Email':
                classes.push('fa-envelope');
                if (
                    this.selected().selected &&
                    this.selected().item.includes('email')
                ) {
                    classes.push('fa-solid');
                }
                break;
            default:
                classes.push('fa-message-lines');
                if (
                    this.selected().selected &&
                    this.selected().item.includes('messaging')
                ) {
                    classes.push('fa-solid');
                }
                break;
        }

        if (!classes.includes('fa-solid')) classes.push('fa-light');

        return classes.join(' ');
    }

    action(type: MediaType) {
        if (this.disabled()) return;

        if (this.mode().includes('communicate')) {
            if (type === 'Voice' && !this.#router.url.includes('comms/voice')) {
                const number = this.item().phoneNumber;
                if (number) {
                    const encoded = btoa(number);
                    this.#router.navigate(['comms', 'voice', 'new', encoded]);
                }
            } else if (
                this.source() === 'end-customers' &&
                type === 'Email' &&
                !this.#router.url.includes('comms/email')
            ) {
                const id = this.item().id;
                this.#router.navigate(['comms', 'email', 'new'], {
                    queryParams: { customer: id },
                });
            } else if (
                this.source() === 'external-contacts' &&
                type === 'Email' &&
                !this.#router.url.includes('comms/email')
            ) {
                const email = this.item().email;
                this.#router.navigate(['comms', 'email', 'new'], {
                    queryParams: {
                        contact: encodeURIComponent(this.item().object),
                        email,
                    },
                });
            }
        } else if (this.mode().includes('emit') && type === 'Voice') {
            this.selectedChange.emit({ type: 'voice', item: this.item() });
        } else if (this.mode().includes('emit') && type === 'Email') {
            this.selectedChange.emit({ type: 'email', item: this.item() });
        }
    }
}
